import { Home, DollarSign, TrendingUp, Calculator, Scale, Clock, Gavel, Users, FileCheck, Heart, HelpCircle, Shield, Landmark, BarChart3, Handshake } from 'lucide-react';

export const residentialSellerArticles = [
  {
    id: 'how-much-is-my-house-worth-brisbane',
    category: 'Seller Guide',
    title: 'How Much is My House Worth in Brisbane?',
    excerpt: 'Free methods to estimate your Brisbane property value, from online tools to professional appraisals.',
    date: '2026-04-01',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=85',
    icon: DollarSign,
    metaDescription: 'Find out how much your house is worth in Brisbane. Compare online valuation tools, comparable sales, and professional appraisals to get an accurate property estimate.',
    keyFacts: [
      'Brisbane median house price reached $950,000 in 2026, with inner-ring suburbs exceeding $1.2M',
      'Online valuation tools (CoreLogic, Domain, REA) provide estimates within 10-20% accuracy — not precise',
      'Comparable sales within 500m and 6 months provide the most reliable basis for pricing in Brisbane',
      'Properties with development potential (zoned MDR+, large block) may be worth 30-80% more than comparable houses',
      'ACRES provides free property assessments for Brisbane homeowners — call 07 3096 0542 or visit acres.au',
    ],
    content: `
## What Determines Your Brisbane Home's Value?

Every property is unique, but Brisbane house values are driven by a predictable set of factors. Understanding these helps you set realistic expectations before you go to market.

### Location and Suburb Performance

Brisbane's property market varies dramatically suburb by suburb. Inner-city suburbs like Woolloongabba, West End, and New Farm command premiums due to proximity to the CBD, lifestyle amenities, and limited supply. Meanwhile, growth corridors through Logan and Moreton Bay offer different value propositions.

The median house price in your suburb is your starting benchmark. Properties above or below the median will vary based on individual characteristics.

### Land Size and Zoning

In Brisbane, land is often worth more than the house sitting on it. A 600m² block in a Medium Density Residential zone could be worth significantly more than a similar house on a smaller block zoned Low Density. Check your zoning at Brisbane City Council's online mapping tool.

### Condition and Presentation

Renovated homes consistently sell for more. A modern kitchen and bathroom can add $50,000-$150,000 to a Brisbane home's value. Even cosmetic updates like painting, landscaping, and decluttering can shift the sale price by 5-10%.

### Infrastructure and Development

Proximity to the new Cross River Rail stations, the Brisbane Metro, and 2032 Olympic venues is already influencing values. Suburbs like Woolloongabba (Gabba Olympic Stadium), Albion (Albion Olympic Hub), and Toowong are seeing accelerated growth.

## Three Ways to Estimate Your Property's Value

### 1. Online Valuation Tools

ACRES offers a free instant property estimate at [acres.au/valuation](/valuation). These tools use comparable sales data and algorithms to provide a price range. They're useful as a starting point but can vary by 10-20% from actual market value.

### 2. Comparable Sales Analysis

This is the method real estate agents use. Look at properties similar to yours that have sold in the last 3-6 months within a 1-2km radius. Adjust for differences in:
- Land size
- Number of bedrooms and bathrooms
- Renovation level
- Aspect and views
- Flood risk or easements

### 3. Professional Market Appraisal

A market appraisal from a local agent is free and provides the most accurate estimate. At ACRES, our appraisals include comparable sales analysis, current buyer demand assessment, and a recommended pricing strategy. Book a complimentary appraisal through our [contact page](/contact).

## Common Mistakes When Valuing Your Home

**Overvaluing renovations.** That $80,000 pool may only add $30,000 to your sale price. Functional improvements (kitchen, bathroom, structural) return more than lifestyle additions.

**Ignoring market conditions.** Brisbane's market moves in cycles. What your neighbour sold for 12 months ago may not reflect today's conditions. Always use current data.

**Confusing land value with house value.** In many Brisbane suburbs, a 40-year-old house on a 800m² block is worth the same (or more) as a renovated house on 400m² — because developers are buying the land.

## What to Do Next

1. Get a free instant estimate at [acres.au/valuation](/valuation)
2. Book a professional market appraisal with our team
3. Compare your suburb's performance against others using our [suburb comparison tool](/compare)
`,
    faq: [
      { q: 'How accurate are online property valuations?', a: 'Online valuations provide a useful estimate but can vary 10-20% from actual market value. They use algorithmic models based on comparable sales. For an accurate figure, get a professional market appraisal from a local agent.' },
      { q: 'Is a bank valuation the same as market value?', a: 'No. Bank valuations are typically conservative — they assess what the property would sell for in a forced sale scenario. Market value reflects what a willing buyer would pay in normal conditions, which is usually higher.' },
      { q: 'How often should I get my property valued?', a: 'If you are considering selling, get a fresh appraisal. Brisbane property values can shift significantly in 6-12 months due to infrastructure announcements, rezoning, and market cycles.' },
      { q: 'Could my house be worth more as a development site?', a: 'If your property is zoned medium or high density, on a large block (600m²+), or near new infrastructure, it could be worth 30-80% more to a developer. ACRES provides free development site assessments — call 07 3096 0542.' },
      { q: 'What is the median house price in Brisbane in 2026?', a: 'The Brisbane median house price is approximately $950,000 as of early 2026, with inner-ring suburbs (within 5km of CBD) typically exceeding $1.2M. Prices vary significantly by suburb.' },
      { q: 'How do I get a free property appraisal in Brisbane?', a: 'ACRES offers free, no-obligation property appraisals across Brisbane and South East Queensland. Visit acres.au or call 07 3096 0542 to request a confidential assessment.' },
    ],
  },
  {
    id: 'best-real-estate-agent-brisbane',
    category: 'Seller Guide',
    title: 'How to Choose the Best Real Estate Agent in Brisbane',
    excerpt: 'What to look for when selecting an agent, and the questions you should ask before signing.',
    date: '2026-03-28',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1556157382-97eda2d62296?w=800&q=85',
    icon: Users,
    metaDescription: 'How to choose the best real estate agent in Brisbane. Compare agent types, commission structures, and the critical questions to ask before listing your property.',
    keyFacts: [
      'Average real estate agent commission in Brisbane ranges from 2.0-2.8% of the sale price',
      'The top 10% of Brisbane agents sell properties 15-25% faster and for 5-10% higher prices than average',
      'Key questions to ask: How many properties in my suburb have you sold? What is your average days on market?',
      'Property advisory firms like ACRES offer a different model — acting in the seller\'s best interest',
      'Always compare at least 3 agents and check their verified sales record before signing',
    ],
    content: `
## Why Your Choice of Agent Matters More Than You Think

The difference between a good agent and a great agent can be $50,000-$200,000 on a Brisbane property sale. Yet most sellers choose their agent based on who quotes the highest price or charges the lowest commission. Both are traps.

### The High-Price Trap

Some agents deliberately overquote your property's value to win the listing. They'll tell you your home is worth $1.2M when comparable sales suggest $1.05M. You list at $1.2M, get no offers for 8 weeks, reduce to $1.1M, and eventually sell for $1M. A property that sits on the market loses its freshness and buyer urgency.

### The Low-Fee Trap

An agent charging 1.5% instead of 2.5% saves you $10,000 on a $1M sale. But if the cheaper agent achieves $950,000 instead of $1,020,000, you've lost $60,000 while saving $10,000. Commission should be evaluated against results, not in isolation.

## What to Look for in a Brisbane Agent

### 1. Local Market Knowledge

Your agent should know your suburb intimately — recent sales, upcoming developments, buyer demographics, and what drives premium prices in your specific area. Ask them to name the last 5 properties they sold in your suburb and what each achieved.

### 2. Marketing Strategy

A premium marketing campaign costs $5,000-$15,000 but can add significantly more to your sale price. Your agent should present a detailed marketing plan including:
- Professional photography and video
- Floor plans and styling recommendations
- Online presence (realestate.com.au, Domain, social media)
- Database marketing to qualified buyers
- Open home strategy

### 3. Negotiation Track Record

Ask for evidence of above-market results. A strong negotiator can create competitive tension between buyers and push your price beyond expectations. At ACRES, we specialise in private transactions and competitive sales processes designed to maximise seller outcomes.

### 4. Communication Style

You'll be working closely with your agent for 4-12 weeks. Ask about their communication frequency, reporting style, and how they handle price feedback from buyers.

## Questions to Ask Every Agent

1. What is your recommended list price, and how did you arrive at it?
2. Show me 5 comparable sales you've used in your appraisal.
3. What is your average days-on-market for properties like mine?
4. What commission and marketing costs should I budget for?
5. How many active buyers do you have looking in my price range?
6. What happens if my property doesn't sell within the agreed period?
7. Can I speak to three recent sellers you've worked with?

## Agent Types in Brisbane

**Boutique Advisory (like ACRES):** Smaller teams with senior agents handling every listing personally. Higher touch, strategic approach, strong negotiation focus. Best for premium properties and development sites.

**Franchise Agencies:** Large networks with brand recognition and extensive buyer databases. Good for volume markets but service quality varies by individual agent.

**Discount/Online Agents:** Lower fees but typically less marketing, fewer inspections, and less negotiation leverage. May suit simple, high-demand properties.

## Red Flags to Watch For

- Agent can't provide recent comparable sales to justify their price estimate
- No written marketing plan before you sign
- Pushes for an exclusive listing with a 120+ day term
- Won't provide references from recent sellers
- Seems more interested in winning the listing than understanding your property
`,
    faq: [
      { q: 'What commission do real estate agents charge in Brisbane?', a: 'Brisbane agent commissions typically range from 2.0% to 2.8% plus GST. Some agents charge a fixed fee. Always compare commission against the agent\'s track record of results, not just the percentage.' },
      { q: 'Should I use the agent who gives the highest appraisal?', a: 'No. The agent who quotes the highest price is often trying to win your listing. Ask every agent to justify their price with comparable sales data. The most accurate appraisal, not the highest, leads to the best outcome.' },
      { q: 'Can I sell without an agent in Queensland?', a: 'Yes, private sales are legal in Queensland. However, most private sellers achieve lower prices due to limited marketing reach, fewer buyers, and weaker negotiation leverage. The net result after agent fees is usually better with a quality agent.' },
    ],
  },
  {
    id: 'how-to-sell-house-fast-brisbane',
    category: 'Seller Guide',
    title: 'How to Sell Your House Fast in Brisbane',
    excerpt: 'Proven strategies to reduce time on market without sacrificing price.',
    date: '2026-03-25',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&q=85',
    icon: Clock,
    metaDescription: 'How to sell your house fast in Brisbane without underselling. Proven pricing, presentation, and marketing strategies from ACRES property advisors.',
    keyFacts: [
      'Brisbane properties that are priced correctly from day one sell 30-50% faster than overpriced listings',
      'The first 2 weeks on market generate the most buyer interest — first impressions are critical',
      'Professional photography can increase online listing views by 60-90% compared to amateur photos',
      'Spring (September-November) and autumn (March-May) are the strongest selling seasons in Brisbane',
      'ACRES helps Brisbane sellers achieve faster sales through strategic pricing and targeted buyer matching',
    ],
    content: `
## Speed vs Price: You Can Have Both

The biggest myth in real estate is that selling fast means selling cheap. In Brisbane's current market, well-priced, well-presented properties are selling in under 21 days — often above the asking price.

The key is preparation. Properties that drag on the market for months almost always share the same mistakes: overpricing, poor presentation, or weak marketing.

## Step 1: Price It Right From Day One

The first two weeks of your listing generate the most buyer interest. If your price is too high, you miss this window.

**The 5% rule:** If your property is priced more than 5% above its true market value, you'll attract 50% fewer enquiries. Price it at market value or slightly below, and you create competitive tension that often pushes the final price above where it would have landed with a higher asking price.

Work with your agent to analyse comparable sales in suburbs like Fortitude Valley, Kangaroo Point, or wherever your property is located. Use data, not emotion.

## Step 2: Present It to Sell

First impressions are made in the first 8 seconds of a buyer walking through your front door. Focus your preparation budget on:

### Exterior
- Pressure wash driveway and paths
- Fresh mulch and trimmed hedges
- Clean gutters and fascia
- Ensure the front door is immaculate

### Interior
- Declutter ruthlessly — remove 50% of your personal items
- Deep clean everything, especially kitchens and bathrooms
- Fresh white paint on scuffed walls
- Fix every small maintenance issue (dripping taps, sticky doors, cracked tiles)

### Professional Styling

Property styling costs $3,000-$8,000 for a 3-bedroom house but typically adds $15,000-$40,000 to the sale price. In Brisbane's competitive inner-city markets, styled homes sell faster and for more than unstaged equivalents.

## Step 3: Marketing That Creates Urgency

Your agent's marketing campaign should create a sense of urgency and exclusivity:

- **Professional photography** — non-negotiable. Bad photos kill listings
- **Twilight photography** — makes properties look premium
- **Video walkthrough** — captures interstate and overseas buyers
- **"Coming soon" campaign** — build a buyer list before going live
- **Targeted social media ads** — reach active buyers in your price range
- **Agent database** — a good agent has 500+ active buyers

## Step 4: Choose the Right Sale Method

For speed in Brisbane:

**Auction:** Creates a defined deadline. Works best for unique properties in high-demand suburbs. Typical campaign: 4 weeks.

**Set date sale (EOI):** Buyers submit offers by a deadline. Creates competitive tension without the stress of auction day.

**Private treaty with a deadline:** List at an attractive price with "offers closing" within 2-3 weeks.

## What Slows Down Sales

- Overpricing by more than 5%
- Poor photography
- Restricted inspection times
- Difficult access or tenant issues
- Outstanding compliance (smoke alarms, pool certificates)
- Unresolved title or boundary issues

Fix all of these before you list. Your conveyancer should prepare the contract and disclosure documents in advance so you can exchange quickly once a buyer is found.

## Brisbane Fast-Sale Suburbs

Properties in high-demand Brisbane suburbs typically sell within 14-21 days. Suburbs like Spring Hill, Kelvin Grove, and Paddington are seeing particularly strong buyer activity due to transport upgrades and limited new supply.

Check your suburb's average days on market using our [suburb profiles](/suburbs) to benchmark realistic timeframes.
`,
    faq: [
      { q: 'How long does it take to sell a house in Brisbane?', a: 'The Brisbane average is 25-35 days on market. Well-priced properties in high-demand suburbs can sell in under 14 days. Overpriced properties can take 90+ days, which significantly reduces the final sale price.' },
      { q: 'Should I sell at auction to sell faster?', a: 'Auction works well in Brisbane for unique properties and suburbs with strong buyer demand. It creates a deadline and competitive tension. However, a well-run private treaty campaign with a set closing date can be equally effective.' },
      { q: 'Does staging really help sell faster?', a: 'Yes. Styled properties in Brisbane sell 30% faster on average and achieve 5-10% higher prices than unstaged equivalents. The $3,000-$8,000 investment typically returns $15,000-$40,000 in additional sale price.' },
    ],
  },
  {
    id: 'selling-costs-calculator-queensland',
    category: 'Seller Guide',
    title: 'Selling Costs Calculator: What You\'ll Pay in Queensland',
    excerpt: 'A complete breakdown of every cost involved in selling property in Queensland.',
    date: '2026-03-22',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=85',
    icon: Calculator,
    metaDescription: 'Complete breakdown of property selling costs in Queensland. Agent commission, marketing, conveyancing, CGT, and hidden fees explained with real examples.',
    keyFacts: [
      'Total selling costs in Queensland typically range from 3.5-5.5% of the sale price',
      'Agent commission: 2.0-2.8% | Marketing: $3,000-$10,000 | Conveyancing: $800-$1,500',
      'Capital Gains Tax applies to investment properties — primary residences are generally CGT-exempt',
      'Sellers should budget $15,000-$30,000 in total costs for a $700,000 property sale in Brisbane',
      'ACRES provides transparent cost breakdowns upfront — no hidden fees or surprise charges',
    ],
    content: `
## Total Cost of Selling in Queensland

Most sellers focus on the agent's commission but underestimate the total cost of selling. On a $1,000,000 Brisbane property, your total selling costs will typically be $30,000-$50,000. Here's the full breakdown.

## Agent Commission

The largest cost. Brisbane agent commissions range from **2.0% to 2.8% plus GST**.

| Sale Price | Commission (2.5%) | + GST (10%) | Total |
|---|---|---|---|
| $750,000 | $18,750 | $1,875 | $20,625 |
| $1,000,000 | $25,000 | $2,500 | $27,500 |
| $1,500,000 | $37,500 | $3,750 | $41,250 |

Some agents offer tiered structures (e.g., 2% on first $500K, 3% above) or performance bonuses for achieving above-reserve prices. Always negotiate.

## Marketing Costs

Most Brisbane agents charge marketing as a separate cost:

- **Basic package** (photos, online listings): $2,000-$4,000
- **Premium package** (photos, video, drone, styling, social ads): $5,000-$15,000
- **Auction costs** (auctioneer fee): $500-$1,000

Marketing is typically paid upfront regardless of whether the property sells. Clarify this with your agent before signing.

## Conveyancing / Legal Fees

Your solicitor or conveyancer handles the legal transfer:

- **Conveyancer:** $800-$1,500
- **Solicitor:** $1,500-$3,000
- **Discharge of mortgage fee:** $300-$500 (charged by your bank)

## Government Charges

- **Title search:** $20-$50
- **Transfer registration:** Typically paid by the buyer
- **No stamp duty for sellers** in Queensland (buyers pay stamp duty)

## Mortgage Break Costs

If you have a fixed-rate mortgage and sell before the fixed term ends, your bank may charge break costs. These can range from $1,000 to $20,000+ depending on the remaining term and interest rate differential. Check with your lender early.

## Capital Gains Tax (CGT)

If the property is an investment (not your primary residence), you'll pay CGT on the profit:

- **Held less than 12 months:** Full capital gain added to your taxable income
- **Held 12+ months:** 50% CGT discount applies
- **Primary residence:** Exempt from CGT (with some conditions)

Example: You bought an investment property for $600,000 and sell for $900,000. Your capital gain is $300,000. If held for 2+ years, only $150,000 is added to your taxable income.

## Other Potential Costs

- **Repairs pre-sale:** $2,000-$10,000 (fix issues that would reduce offers)
- **Styling/staging:** $3,000-$8,000
- **Building and pest report:** $500-$800 (providing one upfront builds buyer confidence)
- **Pool compliance certificate:** $200-$500
- **Smoke alarm compliance:** $200-$500
- **Moving costs:** $1,000-$3,000

## Example: Total Costs on a $1M Brisbane Sale

| Cost | Amount |
|---|---|
| Agent commission (2.5% + GST) | $27,500 |
| Marketing | $6,000 |
| Conveyancing | $1,200 |
| Mortgage discharge | $400 |
| Pre-sale repairs | $3,000 |
| Styling | $5,000 |
| Compliance certificates | $700 |
| **Total** | **$43,800** |

Your net proceeds: approximately $956,200 (before CGT if applicable).
`,
    faq: [
      { q: 'Do sellers pay stamp duty in Queensland?', a: 'No. In Queensland, stamp duty (transfer duty) is paid by the buyer, not the seller. This is one of the advantages of selling in QLD compared to some other states.' },
      { q: 'Can I negotiate agent commission?', a: 'Yes. Agent commissions are negotiable in Australia. However, focus on the net outcome (sale price minus costs) rather than minimising commission alone. A higher-commission agent who achieves a better price will net you more money.' },
      { q: 'What is the average cost to sell a house in Brisbane?', a: 'Total selling costs in Brisbane typically range from 3-5% of the sale price. On a $1,000,000 property, expect $30,000-$50,000 in total costs including commission, marketing, conveyancing, and preparation.' },
    ],
  },
  {
    id: 'how-to-prepare-home-for-sale',
    category: 'Seller Guide',
    title: 'How to Prepare Your Home for Sale: The Complete Checklist',
    excerpt: 'Room-by-room preparation guide to maximise your sale price.',
    date: '2026-03-20',
    readTime: '9 min read',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=85',
    icon: Home,
    metaDescription: 'Complete home preparation checklist for selling in Brisbane. Room-by-room guide covering repairs, styling, photography, and presentation to maximise your sale price.',
    keyFacts: [
      'Well-presented homes in Brisbane sell for 5-10% more than unprepared comparable properties',
      'Key preparation areas: declutter, fresh paint (neutral tones), garden/lawn maintenance, and deep cleaning',
      'Professional home styling costs $3,000-$8,000 but typically returns 3-5x the investment in sale price',
      'Fix the "first 8 seconds": front yard, entrance, and first room viewed set the buyer\'s price expectation',
      'ACRES provides a free property assessment including presentation advice — call 07 3096 0542',
    ],
    content: `
## The 5-Week Pre-Sale Preparation Plan

The best sale results come from methodical preparation. This checklist covers everything from structural repairs to photography day. Start 5 weeks before your planned listing date.

## Week 1-2: Repairs and Maintenance

### Exterior
- [ ] Repair cracked or uneven pathways
- [ ] Fix broken fence palings or gates
- [ ] Replace damaged roof tiles or rusted guttering
- [ ] Repaint faded exterior walls or trim
- [ ] Service the air conditioning (buyers test it)
- [ ] Ensure all outdoor lighting works
- [ ] Fix any pool equipment or fencing compliance issues

### Interior
- [ ] Fix all dripping taps and running toilets
- [ ] Repair cracked tiles or grout
- [ ] Replace broken door handles, hinges, or locks
- [ ] Fix sticking doors and windows
- [ ] Patch and repaint wall damage
- [ ] Replace burned-out light globes (use warm white throughout)
- [ ] Service smoke alarms (mandatory in Queensland)

**Budget guide:** $2,000-$8,000 for a typical Brisbane home. Every dollar spent on repairs returns $2-$4 at sale.

## Week 2-3: Declutter and Deep Clean

### The 50% Rule

Remove at least 50% of your personal items. This includes:
- Family photos and personal memorabilia
- Excess furniture (aim for spacious, not crowded)
- Kitchen bench appliances (keep only 1-2)
- Bathroom products (minimise to hotel-style essentials)
- Kids' toys and artwork from common areas
- Excess books, magazines, and collections

Rent a storage unit ($150-$300/month) — it's the highest-ROI preparation spend.

### Deep Clean Checklist

- [ ] Professional carpet clean ($200-$400)
- [ ] Window cleaning inside and out ($200-$400)
- [ ] Oven and range hood deep clean
- [ ] Grout cleaning in bathrooms and kitchen
- [ ] Pressure wash exterior surfaces
- [ ] Clean light fittings and ceiling fans

## Week 3-4: Styling and Presentation

### Rooms That Matter Most

**Kitchen:** The #1 room buyers evaluate. If you can't renovate, at minimum:
- Replace old handles with modern ones ($200)
- Deep clean or repaint cabinets
- Clear benchtops completely
- Add a fruit bowl and fresh herbs

**Master bedroom:** Create a hotel-suite feel. Crisp white linen, matching bedside tables, and one statement artwork.

**Bathrooms:** Replace old towels with matching white sets. Add a plant. Ensure grout is white and clean.

**Living areas:** Arrange furniture to maximise space and flow. Remove oversized recliners and excess seating.

### Professional Styling

If your budget allows, professional styling is the single highest-return investment:

| Property Value | Styling Cost | Expected Return |
|---|---|---|
| $600K-$800K | $3,000-$5,000 | +$15,000-$25,000 |
| $800K-$1.2M | $5,000-$8,000 | +$25,000-$40,000 |
| $1.2M+ | $8,000-$15,000 | +$40,000-$80,000 |

## Week 4: Landscaping and Street Appeal

Street appeal is your property's first impression. In Brisbane's subtropical climate:

- Mow and edge all lawns
- Fresh mulch in garden beds (dark brown or black)
- Trim hedges and remove dead plants
- Add 3-5 potted plants at the entry
- Clean or replace the letterbox
- Ensure the front door is freshly painted
- Add a new doormat and house numbers

## Week 5: Photography and Launch

### Photography Day Preparation

- Open all blinds and curtains (natural light is essential)
- Turn on all lights
- Remove cars from the driveway
- Hide bins, hoses, and pool equipment
- Remove pet bowls, beds, and evidence of animals
- Put fresh flowers on the dining table and kitchen bench
- Set the outdoor area as if ready for entertaining

### Photography Standards

Your agent should arrange:
- Professional photographer with wide-angle lens
- 20-30 interior and exterior shots
- Twilight photography (premium feel)
- Drone photography (if the property or location warrants it)
- Floor plan
- Video walkthrough

**Never accept phone photos.** Professional photography is the single most important marketing element — it determines whether buyers click on your listing or scroll past.
`,
    faq: [
      { q: 'How much should I spend preparing my house for sale?', a: 'Budget 1-2% of your expected sale price for preparation. On a $1M property, spending $10,000-$20,000 on repairs, styling, and presentation typically returns 3-5x the investment through a higher sale price.' },
      { q: 'Should I renovate before selling?', a: 'Minor cosmetic renovations (paint, handles, landscaping) almost always pay for themselves. Major renovations (new kitchen, bathroom) are riskier — you may not recoup the full cost. Focus on presentation over renovation.' },
      { q: 'Is professional styling worth the cost?', a: 'Yes. Professionally styled homes in Brisbane sell 30% faster and for 5-10% more than equivalent unstaged properties. It is the highest-return preparation investment you can make.' },
    ],
  },
  {
    id: 'should-i-sell-or-rent-my-property',
    category: 'Seller Guide',
    title: 'Should I Sell or Rent My Property?',
    excerpt: 'A financial framework to help you decide whether to sell now or hold and rent.',
    date: '2026-03-18',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1560520031-3a4dc4e9de0c?w=800&q=85',
    icon: Scale,
    metaDescription: 'Should you sell or rent your property? Financial comparison framework covering rental yield, capital growth, tax implications, and opportunity cost for Australian property owners.',
    content: `
## The Sell vs Rent Decision Framework

This is one of the most common questions property owners face when relocating, upgrading, or downsizing. The answer depends on your financial position, the property's performance, and your personal circumstances.

## When Selling Makes Sense

### 1. Low Rental Yield

If your property's gross rental yield is below 3%, the rental income may not justify the holding costs. Calculate your yield:

**Gross Yield = (Annual Rent ÷ Property Value) × 100**

Example: A $1.2M house in Paddington renting for $750/week = $39,000/year ÷ $1,200,000 = 3.25% gross yield. After expenses (rates, insurance, maintenance, management, vacancy), the net yield drops to approximately 1.5-2%.

### 2. High Capital Growth Already Captured

If your property has appreciated significantly, selling now locks in those gains. Brisbane properties that have doubled in value over 8-10 years may be approaching a plateau. Selling and redeploying the equity could deliver better returns.

### 3. CGT Timing

If the property is your primary residence, you pay zero capital gains tax. The moment you move out and rent it, the CGT clock starts (with a 6-year exemption window). If you're planning to sell within the next few years anyway, selling while it's still your PPOR saves a significant tax bill.

### 4. Maintenance Burden

Older properties in suburbs like Toowong, Kelvin Grove, or Clayfield often require $10,000-$20,000/year in maintenance. If the property is becoming a financial drain, selling to a developer or renovator may be your best exit.

## When Renting Makes Sense

### 1. Strong Growth Outlook

If your suburb is in an infrastructure growth corridor (Cross River Rail, Brisbane Metro, Olympic venues), holding for 3-5 more years could capture significant additional capital growth. Suburbs like Woolloongabba, Albion, and Breakfast Creek are in this category.

### 2. Negative Gearing Benefits

If you're on a high marginal tax rate and the property is negatively geared, the tax deductions can offset the cash flow losses. Run the numbers with your accountant.

### 3. Tight Rental Market

Brisbane's vacancy rate is well below 1% in most suburbs. Strong rental demand means reliable income and the ability to increase rents regularly.

### 4. Long-Term Hold Strategy

If you don't need the capital and can afford the holding costs, Brisbane property has historically delivered 6-8% annual returns (capital growth + yield combined) over 10+ year periods.

## The Numbers You Need

Before deciding, calculate:

1. **Net rental yield after all expenses** (rent minus rates, insurance, maintenance, management, vacancy, loan interest)
2. **Capital gains tax if sold now** (get your accountant to calculate the exact figure)
3. **Opportunity cost** — what could you earn if you sold and invested the equity elsewhere?
4. **Cash flow impact** — can you comfortably afford the holding costs if the property is vacant for 4 weeks?

## A Simple Decision Rule

**Sell if:** Net yield < 2% AND growth outlook is moderate AND you have a better use for the equity.

**Rent if:** Net yield > 3% OR strong growth outlook (infrastructure, rezoning) OR significant CGT liability that would be avoided by waiting.

**Seek advice if:** You're unsure about any of the above. A 30-minute conversation with a property advisor can save you tens of thousands in tax and opportunity cost. [Book a strategy session](/strategy-session) with our team.
`,
    faq: [
      { q: 'What rental yield is good enough to hold?', a: 'A net rental yield above 3% generally justifies holding in Brisbane. Below 2% net, the property is likely underperforming and your capital may work harder elsewhere. Factor in expected capital growth for a complete picture.' },
      { q: 'How long can I rent my home before CGT applies?', a: 'If you move out of your primary residence and rent it, you have a 6-year CGT exemption window. If you sell within 6 years of moving out, you pay no CGT on the gain. After 6 years, CGT applies proportionally.' },
      { q: 'Should I sell before or after retirement?', a: 'Selling before retirement while your marginal tax rate is higher can be advantageous if the property is negatively geared. However, selling after retirement when your income is lower means a lower CGT rate. Consult your accountant.' },
    ],
  },
  {
    id: 'is-now-good-time-to-sell-property-brisbane',
    category: 'Market Analysis',
    title: 'Is Now a Good Time to Sell Property in Brisbane?',
    excerpt: 'Current Brisbane market conditions analysed: supply, demand, interest rates, and what they mean for sellers.',
    date: '2026-03-15',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=85',
    icon: TrendingUp,
    metaDescription: 'Is now a good time to sell property in Brisbane? Analysis of current market conditions, buyer demand, interest rates, and price forecasts for 2026.',
    content: `
## Brisbane Market Conditions: April 2026

Brisbane's property market remains one of the strongest in Australia. Here's an objective assessment of current conditions for sellers.

## Supply and Demand

**Listings are low.** Total active listings across Brisbane remain 15-20% below the 10-year average. New listing volumes are recovering but not keeping pace with buyer demand.

**Buyer demand is strong.** Interstate migration continues at record levels, driven by Queensland's relative affordability, lifestyle, and economic growth. Sydney and Melbourne buyers remain active across Brisbane's inner ring.

**Result for sellers:** Low supply + high demand = seller's market. Properties priced correctly are receiving multiple offers and selling above expectations.

## Interest Rates

The RBA has signalled a stabilisation in the cash rate. For sellers, this means:

- Buyer borrowing capacity is clear and stable
- No sudden rate hikes to shock the market
- Buyers are more confident making purchase decisions

This environment favours selling. Rate uncertainty causes buyers to hesitate; stability encourages them to act.

## Infrastructure Driving Values

Brisbane is in the middle of the largest infrastructure investment cycle in its history:

- **Cross River Rail** — connecting Dutton Park to Bowen Hills via new underground stations at Woolloongabba, Albert Street, Roma Street, and Exhibition
- **Brisbane Metro** — bus rapid transit connecting key corridors
- **2032 Olympic Games** — driving venue construction, transport upgrades, and international attention
- **Queens Wharf** — transforming the CBD riverfront

Suburbs near these projects are seeing accelerated price growth. If your property is in one of these corridors, now is an excellent time to capture the infrastructure premium.

## Price Forecasts

Major forecasters predict Brisbane house price growth of 5-8% for 2026, with inner-city and infrastructure-adjacent suburbs outperforming. Unit markets are also strengthening as house affordability pushes buyers toward apartments.

## When to Wait

Consider waiting if:
- Your suburb has a major infrastructure project completing in 12-24 months (the biggest price jump often happens at completion)
- You're in the middle of a renovation that will significantly increase value
- You need to hold for the 12-month CGT discount threshold

## When to Sell Now

Sell now if:
- You've captured strong gains and want to lock them in
- Your property is in a suburb with rising listings (increasing competition)
- You need to access equity for your next purchase
- Your property is ageing and maintenance costs are escalating
- You're moving interstate or downsizing

## The Bottom Line

Brisbane in April 2026 is a strong seller's market. Conditions are favourable, demand is high, and the infrastructure story is compelling. If you're considering selling in the next 12 months, acting now while listings remain low gives you maximum negotiating leverage.

Get a current market assessment of your property through our [free valuation tool](/valuation) or [book a strategy session](/strategy-session) with our advisory team.
`,
    faq: [
      { q: 'Will Brisbane property prices keep rising?', a: 'Major forecasters predict Brisbane house prices to grow 5-8% in 2026, supported by population growth, low supply, infrastructure investment, and the 2032 Olympics pipeline. Individual suburb performance will vary.' },
      { q: 'Is it better to sell in spring or autumn in Brisbane?', a: 'Spring (September-November) traditionally has the most buyer activity in Brisbane. However, autumn (March-May) often has less seller competition, meaning your property stands out more. Both seasons deliver strong results.' },
      { q: 'Should I wait for the Olympics to sell?', a: 'The Olympic premium is already being priced into Brisbane property, particularly in venue suburbs. Waiting until 2032 is risky — much of the infrastructure benefit will be captured in the next 3-5 years, not at the event itself.' },
    ],
  },
  {
    id: 'auction-vs-private-sale-brisbane',
    category: 'Seller Guide',
    title: 'Auction vs Private Sale: Which Gets a Better Price?',
    excerpt: 'Comparing sale methods to determine which approach maximises your result in Brisbane.',
    date: '2026-03-12',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&q=85',
    icon: Gavel,
    metaDescription: 'Auction vs private sale in Brisbane: which gets a better price? Detailed comparison of both methods covering costs, timelines, buyer psychology, and when to use each.',
    content: `
## The Brisbane Sale Method Debate

In Brisbane, approximately 15-20% of properties sell at auction, compared to 30%+ in Sydney and Melbourne. But that doesn't mean auction is less effective here — it means Brisbane sellers have a genuine choice between methods.

## How Auction Works

A 4-5 week marketing campaign builds to a public bidding event. Key characteristics:

**Advantages:**
- Creates urgency with a defined deadline
- Transparent competitive bidding pushes prices up
- Unconditional sale on the fall of the hammer (no cooling-off period)
- Strong emotional response from buyers in a competitive environment

**Disadvantages:**
- Upfront marketing costs ($5,000-$15,000) regardless of outcome
- Some buyers won't attend auctions
- Reserve price can be missed, requiring post-auction negotiation
- Can feel stressful for sellers

**Best for:** Unique properties, prestige homes, renovated properties in high-demand suburbs like Bulimba, Hawthorne, New Farm, and Paddington.

## How Private Treaty Works

The property is listed at a fixed price or price guide. Buyers submit offers which the seller can accept, reject, or counter.

**Advantages:**
- Attracts the widest buyer pool (some buyers avoid auctions)
- More control over timing
- Can be taken off market discreetly if needed
- Lower upfront marketing commitment

**Disadvantages:**
- Less competitive tension than auction
- Negotiations can be drawn out
- Buyers may try to negotiate below market value
- No defined urgency timeline

**Best for:** Properties in moderate-demand areas, development sites, investor-grade properties, and markets where comparable sales are clear.

## Set Date Sale (Expressions of Interest)

A hybrid approach gaining popularity in Brisbane:

- Marketed for 3-4 weeks with a closing date for offers
- Buyers submit their best offer by the deadline
- Seller reviews all offers and negotiates with the preferred buyer(s)

This method combines auction's deadline urgency with private treaty's flexibility. It works particularly well for development sites where developers need time to assess feasibility before offering.

At ACRES, we frequently use this method for strategic advisory and development site sales, achieving above-market results through structured competition.

## The Data: Which Method Gets Better Prices?

Research consistently shows:

- **Auction clearance rates** in Brisbane hover around 50-60%, meaning roughly half of auctioned properties sell on the day. The rest sell post-auction via negotiation.
- **Premium achieved:** When auctions do sell under the hammer, they typically achieve 5-10% above reserve, driven by competitive bidding.
- **Private treaty:** Well-priced properties with multiple interested buyers can achieve similar premiums through best-and-final offers.

The method matters less than the execution. A skilled agent can create competitive tension in any sale format.

## Which Should You Choose?

**Choose auction if:**
- Your property is unique or has strong emotional appeal
- Your suburb has high buyer demand and low listings
- You want a fast, unconditional sale
- You're comfortable with the upfront marketing investment

**Choose private treaty if:**
- Your property is in a price-sensitive market
- Comparable sales clearly establish the value
- You want to test the market without a public deadline
- Your buyer pool includes developers who need due diligence time

**Choose set date sale if:**
- You want competitive tension without the auction format
- Your property may attract both homebuyers and developers
- You want to maintain confidentiality on the final price
`,
    faq: [
      { q: 'Do auctions get higher prices than private sales?', a: 'Not necessarily. Auctions that sell under the hammer often achieve premiums, but 40-50% of Brisbane auctions pass in. The method is less important than the agent\'s ability to create competitive tension between qualified buyers.' },
      { q: 'What happens if my property passes in at auction?', a: 'If no bids reach your reserve, the highest bidder gets first right to negotiate after the auction. Most passed-in properties sell within 1-2 weeks through post-auction negotiation.' },
      { q: 'Can I switch from private treaty to auction mid-campaign?', a: 'Yes, and it can be effective. If private treaty generates strong interest but no committed offers, switching to auction creates urgency. Discuss this strategy with your agent upfront.' },
    ],
  },
  {
    id: 'what-happens-on-settlement-day-sellers-guide',
    category: 'Seller Guide',
    title: 'What Happens on Settlement Day? A Seller\'s Guide',
    excerpt: 'A step-by-step guide to the settlement process for Queensland property sellers.',
    date: '2026-03-10',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=85',
    icon: FileCheck,
    metaDescription: 'What happens on settlement day when selling property in Queensland. Step-by-step guide covering timelines, documents, final inspections, and what to expect.',
    content: `
## Settlement Day: What to Expect

Settlement is the day ownership officially transfers from you to the buyer. In Queensland, settlement typically occurs 30-90 days after signing the contract (as negotiated). Here's exactly what happens.

## Before Settlement Day

### 2 Weeks Before
- Your conveyancer prepares all transfer documents
- Outstanding rates and body corporate levies are calculated and adjusted
- Your bank prepares the mortgage discharge (if applicable)
- Confirm your new address for mail redirection

### 1 Week Before
- Arrange to disconnect utilities (electricity, gas, water, internet) on settlement day
- Complete any agreed repairs or conditions from the contract
- Remove all personal belongings and rubbish
- Leave the property in the condition agreed (usually "reasonably clean")
- Hand all keys, remotes, and security codes to your agent

### Day Before
- Final clean of the property
- Ensure gardens are maintained
- Meter readings for utilities
- Double-check nothing has been left behind

## On Settlement Day

### Morning (8am-10am)
The buyer (or their representative) conducts the **pre-settlement inspection**. They verify:
- The property is in the same condition as when they signed the contract
- All included items (appliances, curtains, light fittings) are present
- Agreed repairs have been completed
- The property is vacant and clean

### Settlement Process (11am-2pm typically)
Settlement in Queensland is now mostly electronic (PEXA). Your conveyancer and the buyer's conveyancer facilitate:

1. **Transfer of title** — legal ownership transfers to the buyer
2. **Mortgage discharge** — your bank releases the mortgage over the property
3. **Financial settlement** — the buyer's bank transfers the purchase price to your conveyancer's trust account
4. **Adjustments** — rates, water, body corporate levies are adjusted to the settlement date
5. **Distribution** — your conveyancer pays out your mortgage, their fees, agent commission, and distributes the balance to you

### Afternoon (2pm-5pm)
- Your conveyancer confirms settlement is complete
- Sale proceeds are transferred to your nominated bank account (usually arrives same day or next business day)
- Your agent hands keys to the buyer
- Congratulations — you've settled!

## Common Settlement Issues

**Delayed settlement:** If the buyer's finance isn't ready, settlement may be delayed. Your conveyancer can charge penalty interest for each day of delay.

**Failed pre-settlement inspection:** If the buyer finds damage that wasn't there at contract signing, or items are missing, settlement can be delayed until rectified.

**Bank delays:** Mortgage discharges occasionally take longer than expected. Ensure your bank has at least 3 weeks' notice.

## Financial Summary at Settlement

Your conveyancer will provide a settlement statement showing:

| Item | Amount |
|---|---|
| Sale price | $1,000,000 |
| Less: Mortgage payout | -$450,000 |
| Less: Agent commission + GST | -$27,500 |
| Less: Conveyancing fees | -$1,200 |
| Less: Rate adjustments | -$800 |
| **Net proceeds to you** | **$520,500** |

The net proceeds are your final take-home from the sale.
`,
    faq: [
      { q: 'How long after settlement do I get my money?', a: 'In Queensland with electronic settlement (PEXA), funds are typically available in your bank account on the same day or next business day after settlement.' },
      { q: 'Do I need to be present on settlement day?', a: 'No. Your conveyancer handles everything. You just need to have vacated the property and handed keys to your agent before settlement.' },
      { q: 'What if the buyer delays settlement?', a: 'If the buyer fails to settle on the agreed date, your conveyancer can charge penalty interest (typically 10% per annum on the purchase price) for each day of delay. If the delay is prolonged, you may have grounds to terminate the contract.' },
    ],
  },
  {
    id: 'capital-gains-tax-property-australia',
    category: 'Seller Guide',
    title: 'Capital Gains Tax on Property: How Much Will I Pay?',
    excerpt: 'A practical guide to calculating CGT on investment property sales in Australia.',
    date: '2026-03-08',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1554224155-1696413565d3?w=800&q=85',
    icon: Landmark,
    metaDescription: 'How to calculate capital gains tax on property in Australia. CGT discount, exemptions, cost base adjustments, and strategies to minimise your tax bill explained.',
    content: `
## Capital Gains Tax Basics for Property Sellers

Capital Gains Tax (CGT) is one of the largest costs for investment property sellers. Understanding how it works can save you tens of thousands of dollars.

## Who Pays CGT?

- **Investment property sellers:** Yes, CGT applies
- **Primary residence sellers:** No, your main home is CGT-exempt
- **Inherited property sellers:** CGT applies but with special rules
- **Foreign residents:** Different rates and no 50% discount

## How CGT is Calculated

CGT is not a separate tax — your capital gain is added to your taxable income and taxed at your marginal rate.

### Step 1: Calculate Your Capital Gain

**Capital Gain = Sale Price − Cost Base**

Your cost base includes:
- Original purchase price
- Stamp duty paid when you bought
- Legal fees on purchase
- Capital improvements (renovations, extensions — not repairs)
- Real estate agent commission on sale
- Legal fees on sale

### Step 2: Apply the 50% Discount

If you held the property for **12 months or more**, only 50% of the capital gain is added to your taxable income.

### Example Calculation

| Item | Amount |
|---|---|
| Sale price | $900,000 |
| Purchase price | $600,000 |
| Stamp duty (on purchase) | $15,000 |
| Renovation costs | $40,000 |
| Agent commission (on sale) | $22,500 |
| Legal fees (purchase + sale) | $3,000 |
| **Cost base** | **$680,500** |
| **Capital gain** | **$219,500** |
| 50% CGT discount (held 2+ years) | **$109,750** |
| Added to taxable income | **$109,750** |

If your marginal tax rate is 37% (income $120K-$180K), the CGT payable is approximately $40,600.

## CGT Exemptions and Concessions

### Main Residence Exemption
Your primary home is fully CGT-exempt. If you lived in the property before renting it out, you get a 6-year absence rule — you can rent it for up to 6 years and still claim the full exemption when you sell.

### The 6-Year Rule
If you move out and rent the property:
- Sell within 6 years = fully CGT-exempt
- Sell after 6 years = CGT applies proportionally
- You cannot claim another property as your main residence during this period

### Partial Exemption
If you used the property as both a home and an investment (e.g., lived in it for 5 years then rented for 3 years), the CGT is apportioned. Only the investment period attracts CGT.

## Strategies to Minimise CGT

### 1. Maximise Your Cost Base
Keep every receipt for capital improvements. New kitchen, bathroom renovation, deck extension, fencing — all reduce your taxable gain.

### 2. Time Your Sale
Sell in a financial year where your other income is lower. If you're retiring, selling in your first year of retirement when your income drops can significantly reduce your marginal tax rate.

### 3. Hold for 12+ Months
The 50% CGT discount only applies if you've held the property for at least 12 months. If you're close to the 12-month mark, waiting a few weeks can halve your tax bill.

### 4. Offset with Capital Losses
Capital losses from shares, crypto, or other investments can offset your property capital gain. Timing asset sales strategically can reduce your overall tax position.

### 5. Consider a Family Trust
Properties held in a discretionary trust can distribute capital gains to family members on lower tax rates. This requires planning well before the sale.

## Get Professional Advice

CGT on property is complex and the stakes are high. A $900,000 sale can generate a $40,000+ tax bill. Invest $500 in a tax accountant who specialises in property — it's the best money you'll spend.

For a current estimate of your property's value, use our [free valuation tool](/valuation).
`,
    faq: [
      { q: 'Do I pay CGT on my home?', a: 'No. Your main residence (primary home) is fully exempt from capital gains tax in Australia. The exemption also covers up to 2 hectares of adjacent land used for domestic purposes.' },
      { q: 'What is the 50% CGT discount?', a: 'If you hold an investment property for 12 months or more before selling, only 50% of the capital gain is added to your taxable income. This effectively halves your CGT bill.' },
      { q: 'Can I avoid CGT by reinvesting in another property?', a: 'No. Unlike some countries, Australia does not have a "rollover" provision for reinvesting property sale proceeds. CGT is payable regardless of what you do with the proceeds.' },
    ],
  },
  {
    id: 'real-estate-agent-commissions-queensland',
    category: 'Seller Guide',
    title: 'How Real Estate Agent Commissions Work in Queensland',
    excerpt: 'Understand commission structures, negotiations, and what you should actually pay.',
    date: '2026-03-05',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=85',
    icon: Handshake,
    metaDescription: 'How real estate agent commissions work in Queensland. Average rates, fee structures, negotiation tips, and how to evaluate agents on results rather than cost.',
    content: `
## Understanding Agent Fees in Queensland

Real estate commissions in Queensland are fully deregulated — agents can charge whatever the market will bear. Here's how to navigate it.

## Average Commission Rates

| Sale Price | Typical Commission | Total (inc GST) |
|---|---|---|
| Under $500K | 2.5-3.0% + GST | $13,750-$16,500 |
| $500K-$1M | 2.2-2.8% + GST | $12,100-$30,800 |
| $1M-$2M | 2.0-2.5% + GST | $22,000-$55,000 |
| $2M+ | 1.5-2.0% + GST | Negotiated |

These are guides only. Commission is always negotiable.

## Commission Structures

### Fixed Percentage
The most common structure. A flat percentage of the sale price (e.g., 2.5% + GST). Simple and transparent.

### Tiered Commission
A lower rate on the base price and a higher rate on anything above. For example:
- 2% on the first $800,000
- 10% on everything above $800,000

This aligns the agent's incentive with your goal — they earn significantly more by achieving a premium result.

### Fixed Fee
Some discount agents offer fixed fees ($5,000-$10,000 regardless of sale price). Lower cost, but the agent has no financial incentive to maximise your price.

## What's Included in Commission?

Commission should cover:
- Agent's time and expertise
- Access to buyer database
- Open homes and private inspections
- Negotiation on your behalf
- Contract management through to settlement

Commission usually does **not** include:
- Photography and video
- Online advertising (realestate.com.au, Domain)
- Print advertising
- Styling costs
- Auctioneer fee

These marketing costs are typically $3,000-$15,000 on top of commission.

## How to Negotiate Commission

### Do
- Compare 3 agents on results, not just fees
- Ask what the agent achieved for similar properties
- Propose a tiered structure that rewards outperformance
- Negotiate the total package (commission + marketing)

### Don't
- Choose the cheapest agent without comparing results
- Negotiate so hard that the agent deprioritises your listing
- Sign a long exclusive agreement without performance clauses
- Forget that commission is paid on the GST-inclusive amount

## The Real Question: Net Proceeds

Stop focusing on commission and focus on **net proceeds**:

| Scenario | Agent Fee | Sale Price | Your Net |
|---|---|---|---|
| Cheap agent (1.8%) | $19,800 | $1,050,000 | $1,030,200 |
| Quality agent (2.5%) | $27,500 | $1,120,000 | $1,092,500 |

The "expensive" agent costs $7,700 more in commission but delivers $62,300 more in net proceeds.

A good agent is an investment, not a cost.
`,
    faq: [
      { q: 'What is the average real estate commission in Brisbane?', a: 'The average commission in Brisbane is 2.2-2.8% plus GST. For a $1,000,000 property, expect to pay $24,200-$30,800 inclusive of GST. Rates are negotiable and vary by agent and sale price.' },
      { q: 'Can I sell my house without paying commission?', a: 'Yes, you can sell privately without an agent. However, private sellers typically achieve 5-10% lower sale prices due to limited marketing reach and weaker negotiation. The net result after commission is usually better with a quality agent.' },
      { q: 'Are cheap agents worth it?', a: 'Rarely. Research shows discount agents achieve lower sale prices on average. The commission savings are typically offset by a lower sale price. Focus on the agent\'s track record and net outcome for you, not the fee alone.' },
    ],
  },
  {
    id: 'selling-investment-property-tax-timing',
    category: 'Seller Guide',
    title: 'Selling an Investment Property: Tax & Timing Strategies',
    excerpt: 'Strategic considerations for investment property sellers including CGT timing, depreciation recapture, and portfolio rebalancing.',
    date: '2026-03-02',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1460472178825-e5240623afd5?w=800&q=85',
    icon: BarChart3,
    metaDescription: 'Tax and timing strategies for selling investment property in Australia. CGT minimisation, depreciation recapture, financial year timing, and portfolio rebalancing explained.',
    content: `
## Strategic Investment Property Disposal

Selling an investment property is a financial decision, not an emotional one. The timing, structure, and tax strategy around the sale can shift your net outcome by tens of thousands of dollars.

## When to Sell an Investment Property

### Underperformance Signals
- Net yield has dropped below 2% after expenses
- Capital growth has stalled for 2+ consecutive years
- Maintenance costs are escalating (older properties)
- The suburb's demographics or infrastructure outlook has deteriorated
- Body corporate fees are rising faster than rents

### Market Signals
- Your suburb's listings are increasing (more competition ahead)
- Interest rates are stabilising after a hiking cycle (good buyer sentiment)
- Infrastructure projects near your property are nearing completion
- You've captured the majority of the growth cycle

## Tax Timing Strategies

### End of Financial Year Planning
If you sell in June, the capital gain hits your tax return immediately. If you sell in July, you defer the tax payment by 12 months. This doesn't reduce the tax, but improves your cash flow.

### Low-Income Year Selling
If you're planning to:
- Take a career break
- Go on parental leave
- Retire
- Start a business (with initial losses)

Selling in a year where your other income is low can reduce your marginal tax rate from 45% to 30% or lower, saving thousands in CGT.

### The 12-Month Threshold
The 50% CGT discount requires holding for 12 months. If you're close to the 12-month mark, delay the sale. A 2-week delay could halve your tax bill.

## Depreciation and Tax Recapture

If you've been claiming depreciation on your investment property, be aware that depreciation reduces your cost base for CGT purposes.

Example:
- Purchase price: $500,000
- Depreciation claimed over 5 years: $50,000
- Adjusted cost base: $450,000

When you sell for $700,000, your capital gain is $250,000 (not $200,000). This "recapture" of depreciation is often overlooked.

## Portfolio Rebalancing

Selling one property to buy another (or diversify into other assets) is a legitimate strategy. Consider:

1. **Sell low-yield, sell high-growth captured** — dispose of properties that have peaked and redeploy into higher-yielding or emerging markets
2. **Geographic diversification** — if all your properties are in one suburb, selling one and buying in a different market reduces concentration risk
3. **Asset class diversification** — redirecting property equity into shares, bonds, or business can improve your overall risk-adjusted returns

## The Exit Checklist

Before listing your investment property:

1. Get a CGT estimate from your accountant
2. Check your depreciation schedule for cost base impact
3. Review the lease — selling with a tenant affects buyer pool and price
4. Confirm body corporate or council compliance
5. Get a current rental appraisal (buyers want to know the yield)
6. Obtain a [free property valuation](/valuation) to understand current market value
`,
    faq: [
      { q: 'Should I sell my investment property with a tenant?', a: 'It depends on your market. Selling with a tenant limits your buyer pool to investors only (homebuyers want vacant possession). However, an existing tenant and lease can be attractive to investors as it guarantees immediate rental income.' },
      { q: 'Can I claim the selling costs against CGT?', a: 'Yes. Agent commission, legal fees, advertising costs, and styling costs for the sale are all added to your cost base, reducing your capital gain. Keep all receipts.' },
      { q: 'What is the best month to sell an investment property?', a: 'From a tax perspective, selling in July or August means the CGT isn\'t payable until the following financial year (15+ months later). From a market perspective, spring (September-November) typically generates the most buyer activity.' },
    ],
  },
  {
    id: 'deceased-estate-sale-guide',
    category: 'Seller Guide',
    title: 'Deceased Estate Sale: A Step-by-Step Guide',
    excerpt: 'Navigating the sale of a deceased estate in Queensland, from probate to settlement.',
    date: '2026-02-28',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=85',
    icon: Shield,
    metaDescription: 'How to sell a deceased estate in Queensland. Step-by-step guide covering probate, executor duties, CGT implications, and achieving the best price.',
    content: `
## Selling a Deceased Estate in Queensland

Selling a loved one's property is an emotional and legally complex process. This guide walks you through each step to help you navigate it with confidence.

## Step 1: Obtain Probate or Letters of Administration

Before you can sell, you need legal authority:

**If there's a will:** The executor named in the will applies to the Supreme Court of Queensland for a Grant of Probate. This confirms their authority to act.

**If there's no will:** A family member (usually next of kin) applies for Letters of Administration.

**Timeline:** Probate typically takes 4-8 weeks. Some conveyancers can begin preparing for sale during this period.

## Step 2: Assess the Property

As executor, you should:
- Secure the property (change locks if needed)
- Maintain insurance
- Continue paying rates and utilities
- Get a professional market appraisal
- Identify any compliance issues (smoke alarms, pool fencing)
- Check for asbestos or structural issues

If the property has been vacant, check for water damage, pest activity, and ensure it's presentable.

## Step 3: Manage Beneficiaries' Expectations

If there are multiple beneficiaries, align on:
- Whether to sell or retain the property
- Expected price range (based on professional appraisals)
- Timeline for sale
- How costs will be shared
- Choice of agent and sale method

Put agreements in writing to avoid disputes later. If beneficiaries disagree, seek legal mediation early.

## Step 4: Prepare and Market the Property

Deceased estates are often sold "as is," but basic preparation still matters:

- Clear personal belongings (engage a deceased estate clearance service if needed: $1,000-$3,000)
- Basic clean and garden tidy
- Minor repairs if cost-effective
- Professional photography

Buyers of deceased estates often expect a discount. Counter this by presenting the property well and creating competition through proper marketing.

## Step 5: Navigate the Sale

### Disclosure Obligations
In Queensland, sellers must disclose:
- Known defects
- Flood history
- Neighbourhood disputes
- Any encumbrances on the title

As executor, you may not have lived in the property. Disclose what you know and make reasonable enquiries.

### Sale Methods
- **Private treaty** is most common for deceased estates
- **Auction** works if the property is in a desirable location
- **EOI/set date sale** can create competitive tension

### Contract Notes
The contract will be signed by the executor "as executor of the estate of [deceased name]." Your conveyancer handles the specific legal requirements.

## CGT on Deceased Estates

### Property acquired before 20 September 1985
If the deceased bought the property before this date, the beneficiaries' cost base is the market value at the date of death. No CGT applies on any gain up to the date of death.

### Property acquired after 20 September 1985
- If the property was the deceased's main residence and sold within 2 years of death: **CGT-exempt**
- If the property was an investment: CGT applies based on the original purchase price
- If the property was the deceased's main residence but sold after 2 years: partial CGT may apply

### The 2-Year Rule
Executors have 2 years from the date of death to sell the deceased's main residence CGT-free. After 2 years, market value at date of death becomes the cost base and CGT applies on any subsequent gain.

## Choosing an Agent for Deceased Estates

Look for an agent who:
- Has experience with deceased estate sales
- Is sensitive to the family's emotional situation
- Can manage multiple beneficiaries with different views
- Provides clear, honest pricing without overquoting
- Handles the compliance and presentation preparation

[Contact our team](/contact) for a confidential discussion about your situation. We regularly handle deceased estate sales with discretion and care.
`,
    faq: [
      { q: 'How long does it take to sell a deceased estate?', a: 'From death to settlement, a deceased estate sale typically takes 4-8 months: 4-8 weeks for probate, 2-4 weeks for preparation, 4-6 weeks for marketing, and 4-6 weeks for settlement.' },
      { q: 'Do I pay CGT on a deceased estate?', a: 'It depends. If the property was the deceased\'s main residence and is sold within 2 years of death, it is CGT-exempt. Investment properties and sales beyond 2 years may attract CGT.' },
      { q: 'Can the executor live in the deceased\'s property?', a: 'The executor has a duty to act in the best interests of the beneficiaries. Living in the property rent-free could constitute a breach of this duty unless all beneficiaries consent in writing.' },
    ],
  },
  {
    id: 'selling-property-during-divorce',
    category: 'Seller Guide',
    title: 'Selling Property During a Divorce: What You Need to Know',
    excerpt: 'How to navigate property sales during separation, including legal requirements and tax implications.',
    date: '2026-02-25',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&q=85',
    icon: Heart,
    metaDescription: 'Selling property during divorce in Australia. Legal requirements, CGT rollover provisions, timing considerations, and how to achieve a fair outcome for both parties.',
    content: `
## Property Sales During Separation

Property is usually the largest asset in a divorce. How it's handled can significantly impact both parties' financial futures. Here's what you need to know.

## Legal Framework

### Family Law Act
Under Australian family law, property settlement can be resolved by:
1. **Agreement** — both parties agree on how to divide assets
2. **Consent orders** — an agreement approved by the Family Court
3. **Court orders** — a judge decides the division

You have **12 months** after a divorce is finalised to apply for property orders. For de facto relationships, the limit is **2 years** after separation.

### Can One Party Force a Sale?
Either party can apply to the Family Court for orders requiring the property to be sold. The court will consider:
- Whether sale is the most practical outcome
- The financial impact on both parties and children
- Whether one party can buy the other out
- Current market conditions

## Selling Options

### Option 1: Sell and Split
The most common and cleanest option. Sell the property at market value, pay all debts and costs, and divide the net proceeds according to your agreement.

**Advantages:** Clean break, clear valuation, immediate distribution.
**Disadvantages:** Both parties need alternative housing, potential CGT.

### Option 2: One Party Buys the Other Out
One spouse keeps the property and pays the other their share. Requires:
- An agreed valuation (get two independent appraisals)
- The buying party must be able to refinance in their name alone
- A formal property settlement agreement

### Option 3: Deferred Sale
The property is retained (often until children finish school) then sold. Requires a binding agreement on:
- Who lives in the property and pays expenses
- How future value changes are shared
- A defined trigger date for sale

## Tax Implications

### CGT Rollover
Transfers of property between spouses as part of a Family Court order or binding financial agreement are **CGT-exempt** under the rollover provisions. The receiving spouse inherits the original cost base.

This means:
- Transferring to one spouse: no CGT event
- Selling to a third party: CGT applies as normal based on the original purchase details

### Main Residence Exemption
If the property was the family home, the main residence exemption may still apply — even if one spouse has moved out — provided the other spouse continues to live there.

## Practical Considerations

### Choosing a Neutral Agent
Both parties should agree on the selling agent. If you can't agree, each nominate an agent and flip a coin, or ask your lawyers to recommend someone.

Key requirement: the agent must act transparently with both parties, providing equal access to information and feedback.

### Setting the Price
Get **two independent market appraisals**. If they differ by more than 5%, get a third. Use the average as your listing price. This removes the risk of one party manipulating the price.

### Managing Inspections
Coordinate inspection access through the agent, not directly. This reduces conflict and ensures the property is presented well for every inspection.

### Proceeds Distribution
Sale proceeds go to your conveyancer's trust account and are distributed according to your property settlement agreement or court orders. Neither party can unilaterally access the funds.

## Protecting Your Interests

1. **Get independent legal advice** — even if the separation is amicable
2. **Don't sign anything** without your lawyer reviewing it
3. **Don't make major changes** to the property without agreement
4. **Keep records** of all expenses you pay on the property after separation
5. **Don't delay** — property values change and legal positions can shift

If you need a market appraisal for property settlement purposes, [contact our team](/contact). We provide detailed, evidence-based appraisals suitable for family law proceedings.
`,
    faq: [
      { q: 'Can my ex force me to sell our house?', a: 'Yes, through the Family Court. Either party can apply for orders requiring the property to be sold. The court will consider what is just and equitable for both parties.' },
      { q: 'Do I pay CGT when transferring property to my ex-spouse?', a: 'No. Property transfers between spouses under a Family Court order or binding financial agreement qualify for a CGT rollover — meaning no CGT is triggered on the transfer.' },
      { q: 'How is the property value determined in divorce?', a: 'Usually by independent market appraisal (not bank valuation). Both parties should agree on 1-2 independent appraisers. If they can\'t agree, the court can appoint a valuer.' },
    ],
  },
  {
    id: 'how-to-choose-between-multiple-offers',
    category: 'Seller Guide',
    title: 'How to Choose Between Multiple Offers on Your Property',
    excerpt: 'Evaluating competing offers: it\'s not always about the highest price.',
    date: '2026-02-22',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1553729459-afe8f2e2ed08?w=800&q=85',
    icon: HelpCircle,
    metaDescription: 'How to evaluate and choose between multiple offers on your property. Price, conditions, settlement terms, and buyer quality explained for Australian sellers.',
    content: `
## Multiple Offers: A Good Problem to Have

Receiving multiple offers means your pricing and marketing are working. But the highest offer isn't always the best offer. Here's how to evaluate competing bids.

## The Five Factors to Compare

### 1. Price
Obviously important, but consider the **net price** after conditions. An offer of $1.05M subject to building and pest inspection might net less than a clean $1.02M offer if the inspection leads to a price renegotiation.

### 2. Conditions
Common conditions and their risk level:

| Condition | Risk | Typical Timeframe |
|---|---|---|
| Finance approval | Medium | 14-21 days |
| Building & pest | Low-Medium | 7-14 days |
| Sale of buyer's property | High | 30-90 days |
| Due diligence (developers) | Medium | 14-30 days |
| Unconditional | None | Immediate |

An unconditional offer at a slightly lower price is often worth more than a conditional offer at a higher price.

### 3. Settlement Terms
Standard settlement in Queensland is 30-60 days. Consider:
- Does a longer settlement suit you? (gives you time to find your next home)
- Does a shorter settlement suit you? (if you've already purchased)
- Can the buyer be flexible on timing?

### 4. Deposit Amount
A larger deposit signals a more committed buyer. Standard is 5-10% of the purchase price. A buyer offering 10% deposit is more committed than one offering the minimum.

### 5. Buyer Profile
Understanding who is making the offer helps assess reliability:
- **Cash buyer / pre-approved finance:** Lowest risk of falling through
- **First home buyer with conditional approval:** Moderate risk
- **Buyer who needs to sell first:** Highest risk
- **Developer:** May need longer due diligence but often cash-ready

## The Best-and-Final Process

When you have multiple interested buyers, your agent should:

1. Inform all parties that multiple offers exist (without disclosing details)
2. Invite each buyer to submit their "best and final" offer by a set deadline
3. Present all offers to you simultaneously
4. Advise you on the strengths and risks of each

This process ensures transparency and maximises competitive tension.

## Common Mistakes

**Accepting the highest price without reading the conditions.** A $50,000 premium is worthless if the buyer's finance falls through 3 weeks later and you have to relist.

**Not giving all buyers a fair chance.** Your agent has a legal obligation to present all offers. Favouring one buyer without a legitimate reason can create legal issues.

**Delaying decisions.** Buyers who feel strung along will walk away. Aim to make a decision within 24-48 hours of receiving best-and-final offers.

**Ignoring your agent's advice on buyer quality.** Experienced agents can read buyer commitment. A buyer who's inspected three times, asked detailed questions, and has pre-approved finance is more reliable than one who made an offer after a single 10-minute inspection.

## The Decision Framework

Score each offer on:
- **Price (40%):** Net price after likely condition outcomes
- **Certainty (30%):** Likelihood of the offer proceeding to settlement
- **Terms (20%):** Settlement timing, deposit, and flexibility
- **Relationship (10%):** Buyer's communication and engagement level

The highest-scoring offer on this framework is usually the best choice — and it's not always the highest price.
`,
    faq: [
      { q: 'Do I have to accept the highest offer?', a: 'No. You are free to accept any offer or reject all of them. Factors like conditions, settlement timing, deposit amount, and buyer reliability are all legitimate considerations beyond price alone.' },
      { q: 'Can I counter-offer to multiple buyers?', a: 'Yes, but it should be done carefully. Your agent can go back to multiple buyers asking for improved terms. However, you can only formally accept one offer at a time.' },
      { q: 'What if the winning buyer pulls out after I reject others?', a: 'This is the risk of multiple offers. If the winning buyer withdraws, you can re-approach other buyers, but they may have moved on. Your agent should maintain backup buyer relationships until contracts are unconditional.' },
    ],
  },
];
